import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import { lighten, Theme } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { styled } from "@mui/material";
import { SortDirection } from "@mui/material/TableCell/TableCell";
import { Order } from "fe-common/graphql";
import { scrollableStyle } from "../commonStyles";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";

const StyledPaper = styled(Paper)(
  (props: {
    rtlview: boolean;
    fixedheader?: string;
    maxheight?: string | number;
  }) => ({
    position: "relative",
    "& .enhanced-table-container": {
      maxHeight: props.maxheight,
      position: "relative",
      ...scrollableStyle,
    },
    "& tr.enhanced-row": {
      cursor: "pointer",
    },
    "& tr.enhanced-row:hover, tr.enhanced-highlighted-row": {
      backgroundColor: "#f5f5f5",
    },
    "& thead":
      props.fixedheader === "true"
        ? {
            top: 0,
            zIndex: 100,
            position: "sticky",
            background: "#FFFFFF",
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            "&.shadowed": {
              boxShadow: "0 3px 12px -2px rgba(0,0,0,.2)",
            },
          }
        : {},
    "& td": {
      textAlign: "inherit",
      position: "relative",
      fontSize: "13px",
    },
    "& div.enhanced-actions-cell": {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    "& .enhanced-floating-actions": {
      position: "sticky",
      left: props.rtlview ? "0" : "auto",
      right: props.rtlview ? "auto" : "0",
      height: "100%",
    },
    "& .enhanced-floating-actions div.enhanced-actions-cell": {
      position: "absolute",
      display: "none",
      top: 0,
      right: 0,
      height: "100%",
      whiteSpace: "nowrap",
      padding: "0 20px 0 30px",
      overflow: "hidden",
      background:
        "linear-gradient(90deg, rgba(245,245,245,0) 0%, rgba(245,245,245,1) 20%, rgba(245,245,245,1) 100%)",
    },
    "& tr:hover .enhanced-floating-actions div.enhanced-actions-cell, tr.enhanced-highlighted-row .enhanced-floating-actions div.enhanced-actions-cell": {
      display: "flex",
    },
    "& .more-data-row": {
      padding: 8,
      minHeight: 54,
      textAlign: "center",
    },
    "& .deleting-row td:not(.deleting-indicator)": {
      background: "rgba(245, 245, 245)",
      opacity: 0.5,
    },
  }),
);

const DeletingIndicator = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  display: "flex",
  alignItems: "center",
  zIndex: 100,
  padding: "4px 20px 4px 8px",
  fontSize: "12px",
  background:
    "linear-gradient(-90deg, rgba(250, 250, 250, 0) 0%, rgb(250, 250, 250) 20%, rgb(250, 250, 250) 100%)",
});

export interface EnhancedTableColumn {
  id: string;
  name: string;
  enableSort?: boolean;
  customSort?: (a: any, b: any) => number;
  onSort?: () => void;
  align?: "center" | "inherit" | "justify" | "left" | "right";
  render?: (rowData: any) => any;
  hidden?: boolean;
}

interface EnhancedTableHeadProps {
  numSelected: number;
  columns: EnhancedTableColumn[];
  sortBy: SortBy;
  onRequestSort: (property: keyof any) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowCount: number;
  hasActions?: boolean;
  floatingActionButtons?: boolean;
  disabled: boolean;
  className: string;
  deletingRows: number[];
}

export interface SortBy {
  order: Order;
  column: keyof any;
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  title?: string;
  onDeleteSelected?: () => void;
  toolbarActions?: React.ReactElement | React.ReactElement[];
  disabled: boolean;
}

export enum ExpandClickableAreas {
  EntireRow = "Row clicked",
  ArrowIcon = "Expand",
  NavigateTo = "Navigated to",
  TopNav = "Top navigation",
}

interface EnhancedTableProps {
  title?: string;
  data: any[];
  columns?: EnhancedTableColumn[];
  onDelete?: (rowIndexes: number[]) => void;
  onEdit?: (rowData: any, rowIndex: number) => void;
  onSelection?: (rows: any[]) => void;
  rowActions?: (
    rowData: any,
    rowIndex?: number,
  ) => React.ReactElement | React.ReactElement[];
  floatingActionButtons?: boolean;
  toolbarActions?: React.ReactElement | React.ReactElement[];
  sortBy?: SortBy;
  className?: string;
  addEmptyRows?: boolean;
  disabled?: boolean;
  hideChecked?: boolean;
  hidePagination?: boolean;
  hideHeader?: boolean;
  hideToolbar?: boolean;
  noResultsMessage?: string | React.ReactElement;
  clearSelection?: boolean;
  defaultRowsPerPage?: number;
  highlightedRows?: number[];
  rtlview?: boolean;
  fixedHeader?: boolean;
  maxHeight?: string | number;
  /**
   * An array of indices that should be expanded. if set onRowExpanded must be filled too!
   * if passed the component will pass to controlled mode vs stateful
   */
  expandedIndices?: number[];
  onRowExpanded?: (source: ExpandClickableAreas, rowData: any) => void;
  expandedRowContent?: (
    rowData: any,
    onClose: () => void,
  ) => React.ReactElement | React.ReactElement[];
  expandedRowPreview?: (
    rowData: any,
  ) => React.ReactElement | React.ReactElement[];
  expandedRowIndicator?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  hasMore?: boolean;
  infiniteScroll?: boolean;
  deletingRows?: number[];
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export const EXPAND_ICON_ID = "expand-icon";

const EnhancedTableHead = (props: EnhancedTableHeadProps) => {
  const {
    columns,
    onSelectAllClick,
    sortBy,
    numSelected,
    rowCount,
    onRequestSort,
    hasActions,
    className,
    deletingRows,
  } = props;

  return (
    <TableHead className={className}>
      <TableRow>
        {deletingRows?.length > 0 && <TableCell padding="none"></TableCell>}
        {onSelectAllClick && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ "aria-label": "select all desserts" }}
              disabled={props.disabled}
            />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align="left"
            sortDirection={
              sortBy?.column === column.id
                ? (sortBy.order.toLowerCase() as SortDirection)
                : false
            }
          >
            {column.enableSort ? (
              <TableSortLabel
                active={sortBy?.column === column.id}
                direction={
                  sortBy?.column === column.id
                    ? (sortBy.order.toLowerCase() as any)
                    : "asc"
                }
                onClick={() => {
                  onRequestSort(column.id);
                  column.onSort && column.onSort();
                }}
              >
                {column.name}
              </TableSortLabel>
            ) : (
              column.name
            )}
          </TableCell>
        ))}
        {hasActions && (
          <TableCell>{!props.floatingActionButtons && "Actions"}</TableCell>
        )}
      </TableRow>
    </TableHead>
  );
};

const useToolbarStyles = makeStyles((theme?: Theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.mode === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
}));

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const classes = useToolbarStyles();
  const { numSelected, title, onDeleteSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {title && (
        <Typography
          className={classes.title}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {title}
        </Typography>
      )}
      {numSelected > 0 && (
        <>
          <Typography
            className={classes.title}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            {numSelected} selected
          </Typography>
          {onDeleteSelected && (
            <Tooltip title="Delete selected rows">
              <IconButton
                size={"small"}
                aria-label="delete"
                onClick={onDeleteSelected}
                disabled={props.disabled}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
      {props.toolbarActions && (
        <div
          style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}
        >
          {props.toolbarActions}
        </div>
      )}
    </Toolbar>
  );
};

const EnhancedTable = (props: EnhancedTableProps) => {
  const { data = [] } = props;
  const columns = props.columns ? props.columns.filter((c) => !c.hidden) : [];

  if (!columns.length) return;

  const [sortBy, setSortBy] = useState<SortBy>(props.sortBy);
  const [selected, setSelected] = useState<number[]>([]);
  const [expanded, setExpanded] = useState<number[]>(
    props.expandedIndices || [],
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    props.defaultRowsPerPage || 10,
  );
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState<number[]>([]);

  const [shadowedHeader, setShadowedHeader] = useState(false);

  const tableContentRef = useRef(null);

  useEffect(() => {
    if (props.clearSelection) setSelected([]);
  }, [props.clearSelection]);

  useEffect(() => {
    setExpanded(props.expandedIndices);
  }, [props.expandedIndices]);

  useEffect(() => {
    if (props.onSelection) {
      const selectedData = data.filter((row, i) => selected.includes(i));
      props.onSelection(selectedData);
    }
  }, [selected]);

  useEffect(() => {
    if (props.sortBy) {
      setSortBy(props.sortBy);
    }
  }, [props.sortBy]);

  const handleRequestSort = (property: keyof any) => {
    const isAsc = sortBy?.column === property && sortBy?.order === Order.Asc;
    setSortBy({ column: property, order: isAsc ? Order.Desc : Order.Asc });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map((n, index) => index);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleRowClick = (event: React.MouseEvent<any>, rowNum: number) => {
    if (props.expandedRowContent || props.onRowExpanded)
      handleExpand(event, rowNum);
    else handleSelect(rowNum);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSelect = (rowNum: number) => {
    if (props.disabled) return;
    const selectedIndex = selected.indexOf(rowNum);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, rowNum);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleExpand = (
    event: React.MouseEvent<HTMLAnchorElement>,
    rowNum: number,
  ) => {
    if (props.onRowExpanded) {
      const source =
        event?.currentTarget.getAttribute("id") === EXPAND_ICON_ID
          ? ExpandClickableAreas.ArrowIcon
          : ExpandClickableAreas.EntireRow;
      props.onRowExpanded(source, sortedData[rowNum]);
      return;
    }

    const expandedIndex = expanded?.indexOf(rowNum);
    let newExpanded: number[] = [];

    if (expandedIndex) {
      if (expandedIndex === -1) {
        newExpanded = newExpanded.concat(selected, rowNum);
      } else if (expandedIndex === 0) {
        newExpanded = newExpanded.concat(selected.slice(1));
      } else if (expandedIndex === selected.length - 1) {
        newExpanded = newExpanded.concat(selected.slice(0, -1));
      } else if (expandedIndex > 0) {
        newExpanded = newExpanded.concat(
          selected.slice(0, expandedIndex),
          selected.slice(expandedIndex + 1),
        );
      }
    }
    setExpanded(newExpanded);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const confirmDeleteBulk = () => {
    setDeleteRowIndex(selected);
    setOpenConfirmDialog(true);
  };
  const confirmDeleteRow = (rowIndex) => {
    setDeleteRowIndex([rowIndex]);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    const customCompare =
      orderBy && props.columns.find((c) => c.id === orderBy).customSort;
    return order === Order.Desc
      ? (a, b) =>
          customCompare
            ? customCompare(a, b)
            : descendingComparator(a, b, orderBy)
      : (a, b) =>
          customCompare
            ? -customCompare(a, b)
            : -descendingComparator(a, b, orderBy);
  }

  const isSelected = (rowNum: number) => selected.includes(rowNum);
  const isExpanded = (rowNum: number) => expanded.includes(rowNum);
  const handleScroll = (e) => {
    const scrolledObj = e.target;
    if (props.fixedHeader) {
      setShadowedHeader(scrolledObj.scrollTop > 0);
    }
    if (props.infiniteScroll && !props.loadingMore) {
      if (
        props.hasMore &&
        scrolledObj.scrollHeight -
          scrolledObj.scrollTop -
          tableContentRef.current.clientHeight <
          1
      )
        props.onLoadMore();
    }
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

  const hasActions: boolean =
    !!props.rowActions ||
    !!props.onDelete ||
    !!props.onEdit ||
    !!props.expandedRowContent;

  let sortedData = stableSort(
    data,
    getComparator(sortBy?.order, sortBy?.column),
  );
  if (!props.hidePagination)
    sortedData = sortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

  return (
    <StyledPaper
      className={props.className + " enhanced-table-paper"}
      rtlview={props.rtlview}
      fixedheader={props.fixedHeader && "true"}
      maxheight={props.maxHeight}
      ref={tableContentRef}
    >
      {!props.hideToolbar && (
        <EnhancedTableToolbar
          numSelected={selected.length}
          title={props.title}
          onDeleteSelected={props.onDelete && confirmDeleteBulk}
          toolbarActions={props.toolbarActions}
          disabled={props.disabled}
        />
      )}
      <TableContainer
        className={"enhanced-table-container"}
        onScroll={handleScroll}
      >
        <Table
          aria-labelledby="tableTitle"
          size="medium"
          aria-label="enhanced table"
        >
          {!props.hideHeader && (
            <EnhancedTableHead
              columns={columns}
              numSelected={selected.length}
              sortBy={sortBy}
              onSelectAllClick={!props.hideChecked && handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              hasActions={hasActions}
              floatingActionButtons={props.floatingActionButtons}
              disabled={props.disabled}
              className={shadowedHeader ? "shadowed" : ""}
              deletingRows={props.deletingRows}
            />
          )}
          <TableBody>
            {sortedData.map((row, rowIndex) => {
              const isItemSelected = isSelected(rowIndex);
              const expandedRowPreview =
                props.expandedRowPreview && props.expandedRowPreview(row);
              const isDeleted = props.deletingRows?.includes(rowIndex);
              return (
                <React.Fragment key={"row" + rowIndex}>
                  <TableRow
                    hover
                    onClick={(event) => handleRowClick(event, rowIndex)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    selected={isItemSelected}
                    className={`enhanced-row
                        ${
                          props.expandedRowContent && isExpanded(rowIndex)
                            ? " enhanced-active-row"
                            : ""
                        }
                        ${
                          props.highlightedRows?.includes(rowIndex)
                            ? " enhanced-highlighted-row"
                            : ""
                        }
                        ${isDeleted ? " deleting-row" : ""}
                    `}
                  >
                    {props.deletingRows?.length > 0 && (
                      <TableCell
                        padding="none"
                        className={"deleting-indicator"}
                      >
                        {isDeleted && (
                          <DeletingIndicator>Deleting...</DeletingIndicator>
                        )}
                      </TableCell>
                    )}
                    {!props.hideChecked && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(rowIndex);
                          }}
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": `enhanced-table-checkbox-${rowIndex}`,
                          }}
                          disabled={props.disabled}
                        />
                      </TableCell>
                    )}
                    {columns.map((column, colIndex) => (
                      <TableCell
                        align={column.align ? column.align : "inherit"}
                        key={column.id + colIndex}
                      >
                        {column.render ? column.render(row) : row[column.id]}
                      </TableCell>
                    ))}
                    {hasActions && (
                      <TableCell
                        align={props.rtlview ? "left" : "right"}
                        className={
                          props.floatingActionButtons
                            ? "enhanced-floating-actions"
                            : ""
                        }
                      >
                        <div className={"enhanced-actions-cell"}>
                          {props.rowActions && props.rowActions(row, rowIndex)}
                          {props.expandedRowIndicator &&
                            props.expandedRowContent && (
                              <IconButton
                                size={"small"}
                                id={EXPAND_ICON_ID}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(e, rowIndex);
                                }}
                              >
                                {isExpanded(rowIndex) ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            )}
                          {props.onEdit && (
                            <IconButton
                              size={"small"}
                              onClick={(e) => {
                                e.stopPropagation();
                                props.onEdit(row, rowIndex);
                              }}
                              disabled={props.disabled}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {props.onDelete && (
                            <IconButton
                              size={"small"}
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDeleteRow(rowIndex);
                              }}
                              disabled={props.disabled}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {props.expandedRowContent && isExpanded(rowIndex) ? (
                    <TableRow
                      className="enhanced-table-expanded"
                      key={"expanded" + rowIndex}
                    >
                      <TableCell colSpan={columns.length + 2}>
                        {props.expandedRowContent(row, () =>
                          handleExpand(null, rowIndex),
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    expandedRowPreview && (
                      <TableRow
                        className="enhanced-table-expanded-preview"
                        key={"preview" + rowIndex}
                        onClick={(e) => handleRowClick(e, rowIndex)}
                      >
                        <TableCell colSpan={columns.length + 2}>
                          {expandedRowPreview}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </React.Fragment>
              );
            })}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 2}>
                  <div
                    style={{
                      padding: 20,
                      textAlign: "center",
                    }}
                  >
                    {props.noResultsMessage || "No data"}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {props.addEmptyRows && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={columns.length + (hasActions ? 2 : 1)} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        {props.loadingMore ? (
          <div className="more-data-row">
            <ThreeDotsLoader />
          </div>
        ) : (
          props.hasMore &&
          !props.infiniteScroll && (
            <div className="more-data-row">
              <Button variant={"text"} onClick={props.onLoadMore}>
                Show more
              </Button>
            </div>
          )
        )}
      </TableContainer>
      {!props.hidePagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      {props.onDelete && (
        <Dialog
          open={openConfirmDialog}
          onClose={handleCloseConfirmDialog}
          aria-labelledby="confirm-delete-dialog-title"
          aria-describedby="confirm-delete-dialog-description"
        >
          <DialogTitle id="confirm-delete-dialog-title">
            Delete row{deleteRowIndex.length > 1 ? "s" : ""}?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-delete-dialog-description">
              Do you want to delete{" "}
              {deleteRowIndex.length > 1 ? "the selected rows" : "this row"}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseConfirmDialog}
              color="secondary"
              autoFocus
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (deleteRowIndex.length > 1) {
                  props.onDelete([...selected]);
                  setSelected([]);
                } else props.onDelete(deleteRowIndex);
                handleCloseConfirmDialog();
              }}
              color="primary"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </StyledPaper>
  );
};

export default EnhancedTable;
